import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { FaStar } from "react-icons/fa";
import { CiCamera } from "react-icons/ci";
import { BsDot } from "react-icons/bs";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postPhotoFetcher, postFetcher } from "../../../api/fetchers.ts";
import { endpoints } from "../../../api/endpoints.ts";
import { useLanguage } from "../../context/LanguageContext.tsx";

interface ProductRateFormProps {
  groupOrders: {
    id: string;
    productImage: string;
    brand: string;
    supplier: string;
    productInfo: {
      productId: string;
      productName: string;
      images: string;
    };
    supplierInfo: {
      supplierId: string;
      supplierMarketPlaceName: string;
    };
  }[];
}

interface ProductRateFormValues {
  productRatings: { [key: string]: number };
  comments: { [key: string]: string };
  images: {
    [key: string]: {
      image1: string;
      image2: string;
      image3: string;
    };
  };
}

const ProductRateForm: React.FC<ProductRateFormProps> = ({ groupOrders }) => {
  const {t} = useLanguage();
  const productValidationSchema = Yup.object().shape({
    productRatings: Yup.object().test(
      "productRatings",
      "All products must be rated",
      (obj: Record<string, number>) => {
        return Object.values(obj).every((rating) => rating > 0);
      }
    ),
    comments: Yup.object().test(
      "comments",
      "All products must have a comment",
      (obj: Record<string, string>) => {
        return Object.values(obj).every(
          (comment) => typeof comment === "string" && comment.trim() !== ""
        );
      }
    ),
  });

  const initialValues: ProductRateFormValues = {
    productRatings: {},
    comments: {},
    images: groupOrders.reduce(
      (acc, order) => ({
        ...acc,
        [order.id]: { image1: "", image2: "", image3: "" },
      }),
      {}
    ),
  };

  {
    /* IMAGE UPLOAD FUNCTION */
  }
  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    orderId: string,
    imageIndex: number,
    setFieldValue: (field: string, value: any) => void
  ) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      if (fileExtension === "svg") {
        toast.error(
         t('toastMessage', 'rate9')
        );
        return;
      }
      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await postPhotoFetcher(
          endpoints.updateImage.updateImageSingle,
          formData
        );
        if (response && response.data && response.data.url) {
          setFieldValue(
            `images.${orderId}.image${imageIndex + 1}`,
            response.data.url
          );
          toast.success(t('toastMessage', 'rate10'));
        } else {
          toast.error(t('toastMessage', 'rate11'));
        }
      } catch (error) {
        toast.error(t('toastMessage', 'rate11'));
        console.error("Error uploading photo:", error);
      }
    }
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={productValidationSchema}
        onSubmit={async (values, { resetForm }) => {
          for (const order of groupOrders) {
            const productData = {
              productId: order.productInfo.productId,
              supplierId: order.supplierInfo.supplierId,
              rating: values.productRatings[order.id],
              review: values.comments[order.id],
              images: values.images[order.id],
            };

            try {
              const response = await postFetcher(
                endpoints.products.postProductEvaluation,
                {
                  arg: {
                    productId: productData.productId,
                    supplierId: productData.supplierId,
                    rating: productData.rating,
                    review: productData.review,
                    images: productData.images,
                  },
                }
              );
              if (response.result) {
                toast.success(
                  `${order.productInfo.productName} ${t('toastMessage', 'rate13')}`
                );
              } else {
                let errorMessage =
                  response?.message ||
                  t('toastMessage', 'rate14');

                if (errorMessage === "Please provide all required fields.") {
                  errorMessage = "Lütfen tüm gerekli alanları doldurun.";
                  return toast.error(`${errorMessage}`);
                } else if (
                  errorMessage === "Product evaluation already exists."
                ) {
                  errorMessage = "Bu ürünü zaten değerlendirdiniz.";
                }
                return toast.error(
                  `${order.productInfo.productName} için ${errorMessage}`
                );
              }
            } catch (error: any) {
              const errorMessage =
                error.response?.data?.message ||
                t('toastMessage', 'rate14');
              toast.error(
                `${order.productInfo.productName} için ${errorMessage}`
              );
              console.error("Error submitting evaluation:", error);
            }
          }
          resetForm();
        }}
      >
        {({ values, setFieldValue }) => (
          <Form>
            {groupOrders.map((order) => (
              <div key={order.id} className="mb-6 mt-6 flex flex-col md:p-12">
                {/* PRODUCT IMAGE AND DETAILS */}
                <div className="mb-6 flex items-center">
                  <img
                    src={order.productInfo.images[0]}
                    alt={order.productInfo.productName}
                    className="w-24 h-auto object-cover mb-4 sm:mb-0 sm:mr-4"
                  />
                  <div>
                    <h3 className="font-bold text-lg sm:text-xl text-primary-color">
                      {order.productInfo.productName}
                    </h3>
                    <p className="text-xs sm:text-sm text-color-opacity mt-2 flex items-center">
                      <BsDot className="text-xl" />
                      <span>
                        Ürün Açıklaması: Bu ürün{" "}
                        {order.supplierInfo.supplierMarketPlaceName} tarafından
                        gönderilecektir.
                      </span>
                    </p>
                    <p className="text-xs sm:text-sm text-color-opacity mt-2 flex items-center">
                      <BsDot className="text-xl" />
                      <span>
                        Kampanya fiyatından satılmak üzere 5 adetten az stok
                        bulunmaktadır.
                      </span>
                    </p>
                    <p className="text-xs sm:text-sm text-color-opacity mt-2 flex items-center">
                      <BsDot className="text-xl" />
                      <span>
                        İncelemiş olduğunuz ürünün satış fiyatını satıcı
                        belirlemektedir.
                      </span>
                    </p>
                    <p className="text-xs sm:text-sm text-color-opacity mt-2 pl-2">
                      Satıcı:{" "}
                      <span className="text-primary-color">
                        {order.supplierInfo.supplierMarketPlaceName}
                      </span>
                    </p>
                  </div>
                </div>
                {/* PRODUCT RATINGS */}
                <div className="mb-6 border p-2 rounded flex justify-between items-center">
                  <h4 className="font-semibold pl-4 text-sm sm:text-base">
                    ÜRÜNÜ DEĞERLENDİRİNİZ
                  </h4>
                  <div className="flex gap-4">
                    {[...Array(5)].map((_, index) => (
                      <FaStar
                        key={index}
                        className={`cursor-pointer text-3xl sm:text-5xl ${
                          index < (values.productRatings[order.id] || 0)
                            ? "text-yellow-400"
                            : "text-gray-300"
                        }`}
                        onClick={() =>
                          setFieldValue(`productRatings.${order.id}`, index + 1)
                        }
                      />
                    ))}
                  </div>
                  <ErrorMessage
                    name={`productRatings.${order.id}`}
                    component="div"
                    className="text-red-500 text-xs"
                  />
                </div>
                {/* COMMENTS */}
                <div className="mb-6">
                  <h4 className="font-semibold mb-2 text-sm sm:text-base">
                    Yorumunuz
                  </h4>
                  <Field
                    as="textarea"
                    className="w-full p-2 border rounded text-sm sm:text-base"
                    rows={4}
                    name={`comments.${order.id}`}
                    placeholder="Ürün ile ilgili yorumunuzu belirtiniz."
                  />
                  <ErrorMessage
                    name={`comments.${order.id}`}
                    component="div"
                    className="text-red-500 text-xs"
                  />
                </div>
                {/* ADD IMAGE */}
                <div className="mb-6">
                  <h4 className="font-semibold mb-2 text-sm sm:text-base">
                    Fotoğraf Ekle
                  </h4>
                  <div className="flex flex-wrap gap-12">
                    {[0, 1, 2].map((index) => (
                      <label
                        key={index}
                        className="w-20 h-20 sm:w-28 sm:h-28 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer"
                      >
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(event) =>
                            handleImageUpload(
                              event,
                              order.id,
                              index,
                              setFieldValue
                            )
                          }
                        />
                        {values.images[order.id][
                          `image${
                            index + 1
                          }` as keyof (typeof values.images)[typeof order.id]
                        ] ? (
                          <img
                            src={
                              values.images[order.id][
                                `image${
                                  index + 1
                                }` as keyof (typeof values.images)[typeof order.id]
                              ]
                            }
                            alt={`Preview ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <>
                            <CiCamera className="text-2xl sm:text-5xl text-primary-color" />
                            <span className="text-xs text-gray-500 text-center mt-1">
                              Fotoğraf
                              <br />
                              Ekle
                            </span>
                          </>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            {/* SUBMIT BUTTON */}
            {/* <button
                            type="submit"
                            className="bg-primary-color text-white px-4 sm:px-6 py-2 rounded text-sm sm:text-base disabled:opacity-50 w-56"
                            disabled={!Object.values(values.productRatings).every(rating => rating > 0) || Object.values(values.comments).some(comment => comment.trim() === '')}
                        >
                            Değerlendir
                        </button> */}
            <button
              type="submit"
              className="bg-primary-color text-white px-4 sm:px-6 py-2 rounded text-sm sm:text-base disabled:opacity-50 w-56"
              disabled={groupOrders.some(
                (order) =>
                  !values.productRatings[order.id] || 
                  !values.comments[order.id]?.trim()
              )}
            >
              Değerlendir
            </button>
          </Form>
        )}
      </Formik>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  );
};

export default ProductRateForm;
